import React, { useEffect, useRef, useState } from 'react';
import {
  Alert, AlertIcon, Box, Button, Checkbox, Flex, FormControl, FormLabel, Input,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton,
  Select, SimpleGrid, Spinner, Table, TableContainer, Tbody, Td, Text, Th, Thead, Tr, useToast,
} from '@chakra-ui/react';
import xhr from 'xhr';
import useAuth from 'hooks/useAuth';

const FIELDS = [
  { group: 'subject', key: 'enabled_date', label: 'Apertura de materias' },
  { group: 'subject', key: 'date_end', label: 'Cierre de materias' },
  { group: 'subject', key: 'boletin_date', label: 'Fecha impresa del boletín' },
  { group: 'subject', key: 'date_start_final_recuperation', label: 'Apertura de recuperación final del año' },
  { group: 'subject', key: 'date_end_final_recuperation', label: 'Cierre de recuperación final del año' },
  { group: 'recovery', key: 'date_start', label: 'Apertura de recuperación del período' },
  { group: 'recovery', key: 'date_end', label: 'Cierre de recuperación del período' },
];
const PERIODS = { primer: 'Primer período', segundo: 'Segundo período', tercer: 'Tercer período', cuarto: 'Cuarto período' };
const emptyEdits = () => Object.fromEntries(FIELDS.map(f => [`${f.group}.${f.key}`, { mode: 'keep', value: '' }]));
const dateLabel = value => value ? String(value).split('-').reverse().join('/') : 'Sin fecha';
const message = error => error.response?.data?.error?.message || error.message || 'No se pudo completar la operación.';

export default function FechasMaterias() {
  const { user } = useAuth();
  const allowed = user?.role?.type === 'supervisor_admin';
  const toast = useToast();
  const generation = useRef(0);
  const [types, setTypes] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [filters, setFilters] = useState({ programType: '', program: '', period: '' });
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState([]);
  const [edits, setEdits] = useState(emptyEdits);
  const [loading, setLoading] = useState(false);
  const [loadingPrograms, setLoadingPrograms] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [searched, setSearched] = useState(false);
  const [confirmation, setConfirmation] = useState(null);
  const type = types.find(t => String(t.id) === filters.programType);
  const programLabel = type?.type === 'grade' ? 'Grado' : type?.type === 'technical_career' ? 'Carrera técnica' : type?.type === 'course' ? 'Curso' : 'Grado, carrera técnica o curso';

  useEffect(() => {
    if (!allowed) return;
    let cancelled = false;
    xhr('/program-types?pagination[limit]=-1').then(res => {
      if (!cancelled) setTypes((res.data.data || []).filter(t => ['grade', 'technical_career', 'course'].includes(t.type)));
    }).catch(e => { if (!cancelled) setError(message(e)); });
    return () => { cancelled = true; };
  }, [allowed]);

  useEffect(() => {
    setPrograms([]);
    if (!allowed || !type) return;
    let cancelled = false;
    setLoadingPrograms(true);
    xhr(`/${type.type.replace('_', '-')}s?filters[program_type][id][$eq]=${type.id}&pagination[limit]=-1&sort[0]=name:asc`)
      .then(res => { if (!cancelled) setPrograms(res.data.data || []); })
      .catch(e => { if (!cancelled) setError(message(e)); })
      .finally(() => { if (!cancelled) setLoadingPrograms(false); });
    return () => { cancelled = true; };
  }, [allowed, type]);

  const changeFilter = (key, value) => {
    generation.current += 1;
    setFilters(old => ({ ...old, [key]: value, ...(key === 'programType' ? { program: '' } : {}) }));
    setRows([]); setSelected([]); setConfirmation(null); setSearched(false); setError(''); setLoading(false);
  };

  const search = async () => {
    const request = ++generation.current;
    setLoading(true); setError(''); setConfirmation(null);
    try {
      const query = new URLSearchParams(Object.entries(filters).filter(([, value]) => value));
      const res = await xhr(`/subjects/bulk-dates/list?${query}`);
      if (request !== generation.current) return;
      setRows(res.data.rows); setSelected(res.data.rows.map(row => row.id)); setSearched(true);
    } catch (e) { if (request === generation.current) setError(message(e)); }
    finally { if (request === generation.current) setLoading(false); }
  };

  const preview = async () => {
    setBusy(true); setError('');
    try {
      const changes = { subject: {}, recovery: {} };
      for (const field of FIELDS) {
        const edit = edits[`${field.group}.${field.key}`];
        if (edit.mode === 'keep') continue;
        if (edit.mode === 'set' && !edit.value) throw new Error(`Completa la fecha: ${field.label}.`);
        changes[field.group][field.key] = edit.mode === 'clear' ? null : edit.value;
      }
      const request = { filters, ids: selected, changes };
      const res = await xhr.post('/subjects/bulk-dates/preview', request);
      setConfirmation({ ...res.data, request });
    } catch (e) { setError(message(e)); }
    finally { setBusy(false); }
  };

  const apply = async () => {
    setBusy(true); setError('');
    try {
      const res = await xhr.post('/subjects/bulk-dates/apply', { ...confirmation.request, snapshot: confirmation.snapshot });
      setConfirmation(null);
      toast({ title: 'Fechas actualizadas', description: `${res.data.subjectsUpdated} materias y ${res.data.recoveriesUpdated} recuperaciones.`, status: 'success', duration: 6000, isClosable: true });
      await search();
    } catch (e) { setError(message(e)); setConfirmation(null); }
    finally { setBusy(false); }
  };

  if (!allowed) return <Alert status="error"><AlertIcon />Solo Supervisor Admin puede acceder a esta sección.</Alert>;
  const updateCount = (confirmation?.subjectUpdates.length || 0) + (confirmation?.recoveryUpdates.length || 0);
  const previewRows = confirmation ? [
    ...confirmation.subjectUpdates.map(update => ({ ...update, group: 'subject', entity: 'Materia' })),
    ...confirmation.recoveryUpdates.map(update => ({ ...update, group: 'recovery', entity: 'Recuperación del período' })),
  ] : [];

  return <Box maxW="100%">
    <Text fontSize="2xl" fontWeight="bold">Fechas de materias</Text>
    <Text color="gray.600" mb={5}>Selecciona las materias y actualiza sus fechas y las de sus recuperaciones de período.</Text>
    <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} mb={4}>
      <FormControl><FormLabel>Tipo de programa</FormLabel><Select value={filters.programType} onChange={e => changeFilter('programType', e.target.value)} isDisabled={busy} placeholder="Selecciona un tipo">
        {types.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
      </Select></FormControl>
      <FormControl><FormLabel>{programLabel}</FormLabel><Select value={filters.program} onChange={e => changeFilter('program', e.target.value)} isDisabled={!type || loadingPrograms || busy}>
        <option value="">Todos</option>{programs.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
      </Select>{loadingPrograms && <Spinner size="xs" />}</FormControl>
      <FormControl><FormLabel>Período</FormLabel><Select value={filters.period} onChange={e => changeFilter('period', e.target.value)} isDisabled={busy}>
        <option value="">Todos los períodos</option>{Object.entries(PERIODS).map(([key, label]) => <option key={key} value={key}>{label}</option>)}
      </Select></FormControl>
    </SimpleGrid>
    <Button colorScheme="purple" onClick={search} isLoading={loading} isDisabled={!filters.programType || busy}>Buscar materias</Button>
    {error && <Alert status="error" mt={4}><AlertIcon />{error}</Alert>}
    {searched && <>
      <Flex mt={6} mb={3} gap={4} align="center" wrap="wrap">
        <Checkbox isChecked={rows.length > 0 && selected.length === rows.length} isIndeterminate={selected.length > 0 && selected.length < rows.length}
          onChange={e => setSelected(e.target.checked ? rows.map(row => row.id) : [])} isDisabled={busy}>Seleccionar todas</Checkbox>
        <Text>{selected.length} de {rows.length} materias seleccionadas</Text>
      </Flex>
      <Text fontSize="sm" color="gray.600" mb={3}>Las fechas pertenecen a la materia y afectan a todos sus estudiantes, incluso si está vinculada a varios semestres.</Text>
      {!rows.length ? <Text>No hay materias con estos filtros.</Text> : <TableContainer maxH="500px" overflowY="auto" borderWidth="1px" borderRadius="lg">
        <Table size="sm" variant="striped"><Thead><Tr>
          <Th>Selección / materia</Th><Th>Programa / período / semestres</Th>{FIELDS.map(f => <Th key={`${f.group}.${f.key}`} whiteSpace="normal" minW="140px">{f.label}</Th>)}
        </Tr></Thead><Tbody>{rows.map(row => <Tr key={row.id}>
          <Td><Checkbox isChecked={selected.includes(row.id)} isDisabled={busy} onChange={e => setSelected(old => e.target.checked ? [...old, row.id] : old.filter(id => id !== row.id))}>
            <Text whiteSpace="normal" minW="180px">{row.name}</Text><Text fontSize="xs" color="gray.500">ID {row.id}</Text>
          </Checkbox></Td>
          <Td whiteSpace="normal" minW="200px">{row.program}<br />{PERIODS[row.period] || 'Sin período'}<br /><Text fontSize="xs">Semestres: {row.semesters.join(', ') || 'Sin asignar'}</Text></Td>
          {FIELDS.map(f => <Td key={`${f.group}.${f.key}`}>
            {f.group === 'subject' ? dateLabel(row[f.key]) : row.recoveries.length ? row.recoveries.map(r => <Text key={r.id} fontSize="sm">{dateLabel(r[f.key])} · #{r.id}{!r.published ? ' (borrador)' : ''}</Text>) : 'Sin recuperación'}
          </Td>)}
        </Tr>)}</Tbody></Table>
      </TableContainer>}
      {rows.length > 0 && <Box mt={6}>
        <Text fontSize="lg" fontWeight="semibold" mb={3}>Nuevas fechas</Text>
        <SimpleGrid minChildWidth="240px" spacing={4}>{FIELDS.map(field => {
          const key = `${field.group}.${field.key}`;
          const edit = edits[key];
          return <FormControl key={key} borderWidth="1px" borderRadius="lg" p={3}><FormLabel>{field.label}</FormLabel>
            <Select size="sm" value={edit.mode} isDisabled={busy} onChange={e => setEdits(old => ({ ...old, [key]: { ...old[key], mode: e.target.value } }))}>
              <option value="keep">Sin cambios</option><option value="set">Establecer fecha</option><option value="clear">Borrar fecha</option>
            </Select>
            {edit.mode === 'set' && <Input mt={2} type="date" size="sm" value={edit.value} isDisabled={busy} onChange={e => setEdits(old => ({ ...old, [key]: { ...old[key], value: e.target.value } }))} />}
          </FormControl>;
        })}</SimpleGrid>
        <Button mt={4} colorScheme="purple" onClick={preview} isLoading={busy} isDisabled={!selected.length || loading}>Revisar cambios</Button>
      </Box>}
    </>}
    <Modal isOpen={!!confirmation} onClose={() => { if (!busy) setConfirmation(null); }} size="6xl" closeOnOverlayClick={!busy} closeOnEsc={!busy}>
      <ModalOverlay /><ModalContent><ModalHeader>Revisar actualización de fechas</ModalHeader><ModalCloseButton isDisabled={busy} />
        <ModalBody>
          <Text mb={3}>{confirmation?.selectedSubjects} materias seleccionadas. Cambiarán {confirmation?.subjectUpdates.length} materias y {confirmation?.recoveryUpdates.length} recuperaciones.</Text>
          {!!confirmation?.missingRecoveries.length && <Alert status="warning" mb={3}><AlertIcon /><Box>Se omitirán las fechas de recuperación en {confirmation.missingRecoveries.length} materias sin recuperación asociada: {confirmation.missingRecoveries.map(r => r.name).join(', ')}. Sus otros campos sí se actualizarán si los seleccionaste.</Box></Alert>}
          {!updateCount && <Alert status="info"><AlertIcon />Las fechas ya tienen estos valores. No hay cambios para guardar.</Alert>}
          <TableContainer maxH="50vh" overflowY="auto"><Table size="sm"><Thead><Tr><Th>Materia</Th><Th>Registro</Th><Th>Campo</Th><Th>Actual</Th><Th>Nuevo</Th></Tr></Thead>
            <Tbody>{previewRows.flatMap(update => Object.entries(update.after).map(([key, value]) => <Tr key={`${update.group}.${update.id}.${key}`}>
              <Td whiteSpace="normal">{update.name}</Td><Td>{update.entity} #{update.id}</Td><Td whiteSpace="normal">{FIELDS.find(f => f.group === update.group && f.key === key)?.label}</Td>
              <Td>{dateLabel(update.before[key])}</Td><Td fontWeight="bold" color={value === null ? 'red.600' : 'purple.600'}>{value === null ? 'Borrar fecha' : dateLabel(value)}</Td>
            </Tr>))}</Tbody></Table></TableContainer>
        </ModalBody>
        <ModalFooter gap={3}><Button variant="ghost" isDisabled={busy} onClick={() => setConfirmation(null)}>Volver</Button><Button colorScheme="purple" isLoading={busy} isDisabled={!updateCount} onClick={apply}>Guardar cambios</Button></ModalFooter>
      </ModalContent></Modal>
  </Box>;
}
